import { Button, Paper, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { ITruckVisitItem } from '@planning/rt-stores/truckVisit/TruckVisitItem'
import { GateInViewStore } from '@planning/stores/gateControl/GateInViewStore'
import { useTranslate } from '@tolgee/react'
import { EmptyIndicator, OptionsButton } from '@tom-ui/ui'
import { formatDateTime, PATHS } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { useNavigate } from 'react-router'
import { TruckVisitStatus, TruckVisitsViewStore } from '../../Stores/TruckVisitsViewStore'
import { OrderCounts } from '../Atoms/OrderCounts'
import { TimeVisitOnTerminal } from '../Atoms/TimeVisitOnTerminal'
import { TruckCompany } from '../Atoms/TruckCompany'
import { TruckVisitAnnouncedDate } from '../Atoms/TruckVisitAnnouncedDate'
import { TruckVisitDriver } from '../Molecules/TruckVisitDriver'
import { TruckVisitPlate } from '../Molecules/TruckVisitPlate'
import { TruckVisitTableFilter } from '../Molecules/TruckVisitTableFilter'
import { TruckVisitWarnings } from '../Molecules/TruckVisitWarnings'
import { OrderList } from './OrderList'

interface Props {
  store: TruckVisitsViewStore
  gateInViewStore: GateInViewStore
}

export const TruckVisitTable: FC<Props> = observer(({ store, gateInViewStore }) => {
  const navigate = useNavigate()
  const { t } = useTranslate()
  const { appViewStore } = usePlanningStore()

  const columns = computed(() => {
    const tableColumns: MRT_ColumnDef<ITruckVisitItem>[] = [
      {
        accessorKey: 'truckPlate',
        header: t('truckPlate', 'Truck plate'),
        sortingFn: (rowA, rowB) => {
          const plateA = rowA.original.data.identifier?.toLowerCase() || ''
          const plateB = rowB.original.data.identifier?.toLowerCase() || ''
          return plateA.localeCompare(plateB)
        },
        Cell: cell => <TruckVisitPlate plate={cell.row.original.data.identifier} />,
      },
      {
        accessorKey: 'trailerPlate',
        header: t('trailerPlate', 'Trailer plate'),
        sortingFn: (rowA, rowB) => {
          const plateA = rowA.original.data.trailerPlateNumber?.toLowerCase() || ''
          const plateB = rowB.original.data.trailerPlateNumber?.toLowerCase() || ''
          return plateA.localeCompare(plateB)
        },
        Cell: cell => <TruckVisitPlate plate={cell.row.original.data.trailerPlateNumber} />,
      },
      {
        header: t('process', 'Process'),
        Cell: cell => <OrderCounts truckVisit={cell.row.original} />,
      },
      {
        accessorKey: 'truckCompany',
        header: t('transportCompany', 'Transport company'),
        sortingFn: (rowA, rowB) => {
          const nameA = rowA.original.company?.data.name?.toLowerCase() || ''
          const nameB = rowB.original.company?.data.name?.toLowerCase() || ''
          return nameA.localeCompare(nameB)
        },
        accessorFn: row => <TruckCompany truckVisit={row} />,
      },
      {
        accessorKey: 'truckDriver',
        header: t('truckDriver', 'Truck driver'),
        sortingFn: (rowA, rowB) => {
          const nameA = rowA.original.driver?.data.name?.toLowerCase() || ''
          const nameB = rowB.original.driver?.data.name?.toLowerCase() || ''
          return nameA.localeCompare(nameB)
        },
        Cell: cell => <TruckVisitDriver truckVisit={cell.row.original} />,
      },
    ]

    if (store.selectedStatus === TruckVisitStatus.expected) {
      tableColumns.push(
        {
          accessorKey: 'announcedDate',
          header: t('announcedDate', 'Announced date'),
          size: 200,
          sortingFn: (rowA, rowB) => dayjs(rowA.original.data.eta).diff(rowB.original.data.eta),
          Cell: cell => <TruckVisitAnnouncedDate truckVisit={cell.row.original} />,
        },
        {
          header: t('warnings', 'Warnings'),
          Cell: cell => (
            <TruckVisitWarnings
              truckVisit={cell.row.original}
              needsReservation={store.needsReservation}
            />
          ),
        },
        {
          header: '',
          id: `actions_${store.selectedStatus}`,
          size: 100,
          Cell: cell => (
            <OptionsButton
              tooltip={t('moreOptions', 'More options')}
              options={[
                {
                  label: t('edit', 'Edit'),
                  onClick: () => {
                    navigate(`${PATHS.visits.truckVisits.url}/${cell.row.original.id}`)
                  },
                },
                {
                  label: t('cancel', 'Cancel'),
                  onClick: async () => {
                    const isConfirmed = await appViewStore.setOpenConfirmDialog(
                      true,
                      t(
                        'truckAppointmentWillBeCancelledDoYouWantToProceed',
                        'Truck visit will be cancelled. Do you want to proceed?',
                      ),
                      t('cancelVisit', 'Cancel visit'),
                    )

                    if (isConfirmed)
                      try {
                        await store.deleteTruckAppointment(cell.row.original.id)
                        appViewStore.setShowAlert(
                          'success',
                          t('truckAppointmentCancelled', 'Truck visit cancelled'),
                        )
                      } catch (error) {
                        appViewStore.setShowAlert(
                          'error',
                          t('errorCancellingTruckAppointment', 'Error cancelling truck visit'),
                        )
                      }
                  },
                },
              ]}
            />
          ),
        },
      )
    }

    const gateNameColumn = (
      accessorKey: string,
      header: string,
      gateName: (visit: ITruckVisitItem) => string | undefined,
    ): MRT_ColumnDef<ITruckVisitItem> => ({
      accessorKey,
      header,
      sortingFn: (rowA, rowB) => {
        const nameA = gateName(rowA.original)?.toLowerCase() || ''
        const nameB = gateName(rowB.original)?.toLowerCase() || ''
        return nameA.localeCompare(nameB)
      },
      Cell: cell => (
        <Typography variant='subtitle2'>{gateName(cell.row.original) ?? EmptyIndicator}</Typography>
      ),
    })

    if (store.selectedStatus === TruckVisitStatus.arrived) {
      tableColumns.push(
        gateNameColumn('entryGate', t('entryGate', 'Entry gate'), visit => visit.entryGateName),
        gateNameColumn(
          'suggestedExitGate',
          t('suggestedExit', 'Suggested exit'),
          visit => visit.designatedExitGateName,
        ),
        {
          accessorKey: 'timeOnTerminal',
          header: t('timeOnTerminal', 'Time on terminal'),
          sortingFn: (rowA, rowB) => dayjs(rowA.original.data.ata).diff(rowB.original.data.ata),
          Cell: cell => (
            <TimeVisitOnTerminal
              ata={cell.row.original.data.ata}
              truckOverdue={store.truckOverdue}
            />
          ),
        },
        {
          header: '',
          id: `actions_${store.selectedStatus}`,
          size: 100,
          Cell: cell => (
            <Button
              color='primary'
              onClick={() =>
                gateInViewStore.openGateOutConfirmationDialogByTruckVisit(cell.row.original)
              }
            >
              {t('gateOut', 'Gate out')}
            </Button>
          ),
        },
      )
    }

    if (store.selectedStatus === TruckVisitStatus.departed) {
      tableColumns.push(
        gateNameColumn('entryGate', t('entryGate', 'Entry gate'), visit => visit.entryGateName),
        gateNameColumn('exitGate', t('exitGate', 'Exit gate'), visit => visit.exitGateName),
        {
          accessorKey: 'timeOnTerminal',
          header: t('departureTime', 'Departure time'),
          sortingFn: (rowA, rowB) => dayjs(rowA.original.data.atd).diff(rowB.original.data.atd),
          accessorFn: row => (
            <Typography variant='subtitle2'>
              {row.data.atd ? formatDateTime(row.data.atd) : EmptyIndicator}
            </Typography>
          ),
        },
      )
    }

    return tableColumns
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }).get()

  return (
    <Paper elevation={1} sx={{ overflow: 'hidden' }}>
      <TruckVisitTableFilter store={store} />
      <MaterialReactTable
        columns={columns}
        enableDensityToggle={false}
        enableHiding={false}
        enableColumnActions={false}
        enableTopToolbar={false}
        data={store.truckVisitFilteredByStatus}
        localization={{
          noRecordsToDisplay: t('noResultFound', 'No result found'),
          rowsPerPage: t('rowsPerPage', 'Rows per page'),
          of: t('of', 'of'),
        }}
        getRowId={row => row.id.toString()}
        renderDetailPanel={({ row }) => (
          <OrderList
            visit={row.original}
            needsReservation={store.needsReservation}
            canDisplayWarnings={
              store.selectedStatus === TruckVisitStatus.expected && !row.original.data.isCancelled
            }
            onReserveLocation={store.openPlanContainerPositionDialog}
          />
        )}
      />
    </Paper>
  )
})
